import { createAsyncThunk } from "@reduxjs/toolkit";
import { isEmpty, snackWrapper } from "helpers";
import { organizationAPI } from "libs/apiClient/Organization";
import { checkCompletingReferences } from "toolkit/references/references.thunks";
import { organizationsAPI } from "libs/apiClient/Organizations";
import { OrganizationResponse } from "libs/apiClient/data-contracts";

export const getOrganizations = createAsyncThunk(
	"organizationsSlice/getOrganizations",
	async (
		{
			part,
			quantity,
			isCategorization,
			archiveMode,
		}: { part: number; quantity: number; isCategorization: boolean; archiveMode: boolean },
		{ rejectWithValue, dispatch },
	) => {
		try {
			// получение головной организации
			let res: OrganizationResponse[];
			if (isCategorization) {
				res = await organizationsAPI.categorizationList({ page: part, num: quantity }).then((r) => r.data);
			} else {
				res = await organizationsAPI.getOrganizations({ archived: archiveMode }).then((r) => r.data);
			}

			// const res = (await $api<IOrganization[]>(url)).data;

			dispatch(checkCompletingReferences());

			return res;
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const getOrganizationsSearched = createAsyncThunk(
	"organizationsSlice/getOrganizationsSearched",
	async (
		{ query, isCategorization, archiveMode }: { query: string; isCategorization: boolean; archiveMode: boolean },
		{ rejectWithValue },
	) => {
		try {
			let res: OrganizationResponse[];
			if (isCategorization) {
				res = await organizationsAPI.categorizationList({ page: 1, num: 99999, query }).then((r) => r.data);
			} else {
				res = await organizationsAPI.getOrganizations({ archived: archiveMode, query }).then((r) => r.data);
			}
			return res;
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const getSelectedOrganization = createAsyncThunk(
	"organizationsSlice/getSelectedOrganization",
	async (id: number, { rejectWithValue }) => {
		try {
			// return (await $api<IOrganization>(`/organization/${id}`)).data;
			return await organizationAPI.organizationDetail(id).then(({ data }) => {
				return data;
			});
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const getParentSelectedOrganization = createAsyncThunk(
	"organizationsSlice/getParentSelectedOrganization",
	async (id: number, { rejectWithValue }) => {
		try {
			return await organizationAPI.organizationDetail(id).then((r) => r.data);
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const getChildrenOrganization = createAsyncThunk(
	"organizationsSlice/getChildrenOrganization",
	async (
		{
			id,
			part,
			quantity,
			isCategorization,
		}: { id: number; part?: number; quantity?: number; isCategorization: boolean },
		{ rejectWithValue },
	) => {
		try {
			let res: OrganizationResponse[];
			if (isCategorization) {
				res = await organizationsAPI.categorizationChildrenList({ id, page: 1, num: 99999 }).then((r) => r.data);
			} else {
				res = await organizationsAPI.categorizationChildrenList({ id, page: 1, num: 99999 }).then((r) => r.data);
			}
			return res;
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const getSubsidiaries = createAsyncThunk(
	"organizationsSlice/getSubsidiaries",
	async (id: number, { rejectWithValue }) => {
		try {
			return await organizationsAPI.childrenList({ id }).then((r) => r.data);
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const createNewOrganization = createAsyncThunk(
	"organizationsSlice/createNewOrganization",
	async (
		{
			newOrganization,
			setLoading,
			close,
		}: { newOrganization: any; setLoading: (value: boolean) => void; close: () => void },
		{ rejectWithValue, dispatch },
	) => {
		try {
			const response = await organizationsAPI
				.organizationsCreate({
					fullName: newOrganization.fullName,
					inn: newOrganization.inn,
					kpp: newOrganization.kpp,
					ogrn: newOrganization.ogrn,
					parent: newOrganization.parent,
					shortName: newOrganization.shortName,
					type: newOrganization.type,
				})
				.then((r) => r.data);
			close();

			dispatch(checkCompletingReferences());

			return response;
		} catch ({ message }) {
			setLoading(false);
			return rejectWithValue(message);
		}
	},
);

export const archiveOrganization = createAsyncThunk(
	"organizationsSlice/archiveOrganization",
	async ({ org }: { org: OrganizationResponse }, { rejectWithValue, dispatch }) => {
		try {
			await organizationAPI.organizationDelete(org.id, { archived: true });

			dispatch(checkCompletingReferences());

			return org;
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const restoreOrganization = createAsyncThunk(
	"organizationsSlice/restoreOrganization",
	async ({ org }: { org: OrganizationResponse }, { rejectWithValue }) => {
		try {
			await organizationAPI.organizationDelete(org.id, { archived: false });
			return org;
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const editOrganization = createAsyncThunk(
	"organizationsSlice/editOrganization",
	async (
		{
			organization,
			setLoading,
			close,
		}: { organization: any; setLoading?: (value: boolean) => void; close?: () => void },
		{ rejectWithValue },
	) => {
		try {
			// Добавить дату
			const response = await organizationAPI
				.organizationUpdate(organization.id, {
					activityArea: organization.activityArea?.id || null,
					director: organization.director?.id || null,
					fullName: organization.fullName?.trim() || null,
					inn: organization.inn?.trim() || null,
					kpp: organization.kpp?.trim() || null,
					legalAddress: organization.legalAddress?.trim() || null,
					ogrn: organization.ogrn?.trim() || null,
					parent: organization.parent?.id || null,
					phone: organization.phone?.trim() || null,
					postalAddress: organization.postalAddress?.trim() || null,
					propertyType: organization.propertyType?.id || null,
					responsible: organization.responsible?.id || null,
					shortName: organization.shortName?.trim() || null,
					website: organization.website?.trim() || null,
					type: organization.type?.id || null,
					registrationDate: organization.registrationDate,
					securityDescription: organization.securityDescription || "",
				})
				.then((r) => r.data);
			if (close) close();
			return response;
		} catch ({ message }) {
			if (setLoading) setLoading(false);
			return rejectWithValue(message);
		}
	},
);

export const setOrganizationLogo = createAsyncThunk(
	"organizationsSlice/setOrganizationLogo",
	async ({ id, logo }: { id: number; logo: any }, { rejectWithValue }) => {
		try {
			const params = new FormData();
			params.append("file", logo);
			await organizationAPI.logoCreate(id, { file: logo });
			return await organizationAPI.logoDetail(id, { format: "blob" }).then((r) => {
				let blob = new Blob([r.data], { type: r.data.type }); //this make the magic
				return URL.createObjectURL(blob);
			});
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const getOrganizationLogo = createAsyncThunk(
	"organizationsSlice/getOrganizationLogo",
	async (id: number, { rejectWithValue }) => {
		try {
			return await organizationAPI.logoDetail(id, { format: "blob" }).then((response) => {
				let blob = new Blob([response.data], { type: response.data.type }); //this make the magic
				return URL.createObjectURL(blob);
			});
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

// export const setRegistrationDate = createAsyncThunk(
// 	"organizationsSlice/setRegistrationDate",
// 	async ({ organization, id, date }: { organization: any; id: number; date: string }, { rejectWithValue }) => {
// 		try {
// 			// Добавить дату
// 			const response = (
// 				await $api.put<IOrganization>(`/organization/${id}`, {
// 					activityArea: organization.activityArea?.id || null,
// 					director: organization.CEO?.id || null,
// 					fullName: organization.fullName?.trim() || null,
// 					inn: organization.inn?.trim() || null,
// 					kpp: organization.kpp?.trim() || null,
// 					legalAddress: organization.legalAddress?.trim() || null,
// 					ogrn: organization.ogrn?.trim() || null,
// 					parent: organization.parent?.id || null,
// 					phone: organization.phoneNumber?.trim() || null,
// 					postalAddress: organization.postalAddress?.trim() || null,
// 					propertyType: organization.type?.id || null,
// 					responsible: organization.responsible?.id || null,
// 					shortName: organization.shortName?.trim() || null,
// 					website: organization.website?.trim() || null,
// 					type: organization.hierarchyType?.id || null,
// 					securityDescription: organization.securityDescription,
// 					registrationDate: date,
// 				})
// 			).data;
// 			return date;
// 		} catch ({ message }) {
// 			return rejectWithValue(message);
// 		}
// 	},
// );

export const getMarkerList = createAsyncThunk(
	"organizationsSlice/getMarkerList",
	async (body: { id: number }, { rejectWithValue }) => {
		try {
			return await organizationAPI.markersDetail(body.id).then((r) => r.data);
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const setMarkers = createAsyncThunk(
	"organizationsSlice/setMarkers",
	async ({ id, markers }: { id: number; markers: any }, { rejectWithValue, dispatch }) => {
		try {
			const markersIds = markers?.map((marker: any) => marker.id);

			const formData = new FormData();

			formData.append("idlist", JSON.stringify(markersIds));

			const res = await organizationAPI.markersCreate(id, { idlist: markersIds }).then((r) => r.data);

			const user = res.user;
			let system = !isEmpty(res.system) ? res.system.KII : {};
			return { user, system };
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const setDocumentKii = createAsyncThunk(
	"organizationsSlice/setDocumentKii",
	async (
		body: { orgId: number | undefined; file: File | null; setDocumentName: any; setPath: any },
		{ rejectWithValue },
	) => {
		const { orgId, file, setDocumentName, setPath } = body;

		try {
			if (file) {
				const params = new FormData();
				params.append("file", file);
				const key = await organizationAPI.okiiDocCreate(orgId!, { file }).then((r) => r.data);

				setDocumentName(file.name);
				setPath("/files/" + key);

				return { name: file.name, key };
			}
		} catch ({ messages }) {
			return rejectWithValue(messages);
		}
	},
);

export const getCodeList = createAsyncThunk(
	"organizationsSlice/getCodeList",
	async (id: number, { rejectWithValue }) => {
		try {
			return await organizationAPI.okvedDetail(id).then((r) => r.data);
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const switchCategorizationRule = createAsyncThunk(
	"organizationsSlice/switchCategorizationRule",
	async (
		body: {
			orgId: number;
			categorizable: boolean | undefined;
			children?: number | null;
		},
		{ rejectWithValue },
	) => {
		try {
			if (body.categorizable) {
				await organizationAPI
					.categorizableDelete(body.orgId)
					.then(() => snackWrapper("success", "Доступ к категорированию снят"));
			} else {
				await organizationAPI
					.categorizableCreate(body.orgId)
					.then(() => snackWrapper("success", "Доступ к категорированию разрешён"));
			}

			return {
				orgId: body.orgId,
				categorizable: body.children ? body.categorizable : !body.categorizable,
			};
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const getOkiiStatuses = createAsyncThunk(
	"organizationsSlice/getOkiiStatuses",
	async (body: { orgId: string | undefined; query: string }, { rejectWithValue }) => {
		try {
			return await organizationAPI.okiiDetail(+body.orgId!, { query: body.query }).then((r) => r.data);
		} catch ({ message }) {
			return rejectWithValue(message);
		}
	},
);

export const changeStatusCategorization = createAsyncThunk(
	"ogranizations/changeStatusCategorization",
	async (
		body: {
			orgId: string | undefined;
			status: "NOT_CATEGORIZED" | "PRIMARY_STARTED" | "PRIMARY_DONE" | "SECONDARY_STARTED" | "SECONDARY_DONE";
		},
		{ rejectWithValue },
	) => {
		try {
			const res = await organizationAPI
				.categorizationStatusDetail(+body.orgId!, { status: body.status })
				.then((r) => r.data);

			snackWrapper("success", "Статус успешно изменён");

			return res;
		} catch ({ message }) {
			snackWrapper("error", "Не удалось изменить статус");

			return rejectWithValue(message);
		}
	},
);
